import type { FundDetail } from "@/data/fund-detail-types";
import type { useLiveHoldings } from "@/hooks/use-etf-live-holdings";
import {
  EMPTY,
  formatAum,
  formatChangePct,
  formatPrice,
  formatSpreadPct,
  useFundLiveData,
} from "@/hooks/use-fund-live-data";
import type { useLeveragedHoldings } from "@/hooks/use-leveraged-holdings";
import { downloadCsv, rowsToCsv } from "@/lib/csv-download";
import { cn } from "@/lib/utils";
import { useCallback } from "react";
import { InfoTooltip as InfoTooltipIcon } from "./InfoTooltip";

const LIVE_FACT_LABELS: Record<string, "price" | "nav" | "aum" | "premium" | "spread"> = {
  AUM: "aum",
  "Assets Under Management": "aum",
  "Net Assets": "aum",
  NAV: "nav",
  "Last Closing Price": "price",
  "Market Price": "price",
  "Premium/Discount": "premium",
  "Premium / Discount": "premium",
  "30-Day Median Bid/Ask Spread": "spread",
};

// Maps a Fund Facts row label (across all four type-specific builders) to the
// DDB Fund leaf path it displays, so the admin preview can box the exact fact
// that changed. Live-overlay facts (AUM / NAV / Market Price / Premium / Spread)
// have no config path and stay unmarked — the section box still covers them.
const FACT_LABEL_TO_PATH: Record<string, string> = {
  Ticker: "ticker",
  "Expense Ratio": "expenseRatio",
  "Net Expense Ratio": "expenseRatio",
  "Gross Expense Ratio": "expenseGross",
  "Expense Waiver": "expenseRatioDiscount",
  "Base Currency": "baseCurrency",
  "SEC 30-Day Yield": "sec30DayYield",
  "Distribution Frequency": "distributionFrequency",
  Exchange: "exchange",
  "Management Style": "managementStyle",
  "Mgmt. Style": "managementStyle",
  CUSIP: "cusip",
  "Inception Date": "inceptionDate",
  Adviser: "adviser",
  Distributor: "distributor",
  Custodian: "custodian",
  Administrator: "administrator",
  "Transfer Agent": "transferAgent",
  Auditor: "auditor",
  "Leverage Factor": "leverageFactor",
  "Underlying / Reference": "underlyingTicker",
  "Effective Duration": "effectiveDuration",
  "Weighted Avg Maturity": "weightedAvgMaturity",
  "Effective Maturity": "weightedAvgMaturity",
  "Number of Holdings": "holdingsCount",
};

// Union of every config path the Fund Facts table can surface — the coarse box
// that lights up whenever any fact changes (even an unmapped one).
const FUND_FACTS_PATHS =
  "ticker expenseRatio expenseGross expenseRatioDiscount baseCurrency sec30DayYield " +
  "distributionFrequency exchange managementStyle cusip isin lei ein inceptionDate adviser " +
  "distributor custodian administrator transferAgent auditor leverageFactor underlyingTicker " +
  "effectiveDuration weightedAvgMaturity holdingsCount holdingsCountOverride";

const SEC_FILED_DOC_NAMES = new Set([
  "Prospectus",
  "Summary Prospectus",
  "SAI",
  "Annual Report",
  "Fact Sheet",
]);
function isSecFiledDocRestricted(docName: string, hasHref: boolean): boolean {
  if (hasHref) return false;
  return SEC_FILED_DOC_NAMES.has(docName);
}

function formatFactAsOf(input: string | null | undefined): string | null {
  if (!input) return null;
  if (input === "[ ]" || input === "TBD" || input === "Pre-Launch") return null;
  const isIso10 = /^\d{4}-\d{2}-\d{2}$/.test(input);
  const candidate = isIso10 ? `${input}T12:00:00` : input;
  const d = new Date(candidate);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/New_York",
  });
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="font-[family-name:var(--font-instrument)] text-xs leading-[1.5] tracking-[0.16em] uppercase text-[var(--etf-white2)] mb-5 flex items-center gap-2.5">
      {children}
      <span className="flex-1 h-px bg-white/[0.13]" />
    </h3>
  );
}

export function FundDetailsTable({
  fund,
  factsOnly = false,
  liveHoldings,
  leveraged,
  betweenFactsAndDocuments,
}: {
  fund: FundDetail;
  factsOnly?: boolean;
  liveHoldings: ReturnType<typeof useLiveHoldings>;
  leveraged: ReturnType<typeof useLeveragedHoldings>;
  betweenFactsAndDocuments?: React.ReactNode;
}) {
  const live = useFundLiveData(fund.ticker, fund) ?? EMPTY;
  const liveAum =
    liveHoldings.isLive && liveHoldings.aum != null && liveHoldings.aum > 0
      ? liveHoldings.aum
      : null;

  const isLeveraged = fund.type === "leveraged";

  // Fact Sheet has no client-side PDF generation wired up; it is a normal
  // SEC-filed document — enabled as a link only when a real URL is present,
  // otherwise rendered greyed-out/disabled (see SEC_FILED_DOC_NAMES).
  const handleDownloadHoldings = useCallback(() => {
    const slug = fund.ticker.toLowerCase();
    if (isLeveraged) {
      if (!leveraged.rows || leveraged.rows.length === 0) return;
      const dateForFile = (leveraged.asOf || "").replace(/[^A-Za-z0-9]+/g, "-");
      const filename = `${slug}-holdings${dateForFile ? `-${dateForFile}` : ""}.csv`;
      const csv = rowsToCsv(leveraged.rows, [
        { header: "Name", value: (r) => r.name },
        { header: "Type", value: (r) => r.typeLabel },
        { header: "Weight (%)", value: (r) => r.weightPct.toFixed(2) },
        { header: "Market Value", value: (r) => r.marketValue.toFixed(2) },
      ]);
      downloadCsv(filename, csv);
      return;
    }
    const rows = liveHoldings.holdings;
    if (rows.length === 0) return;
    const dateLabel = (liveHoldings.isLive ? liveHoldings.dateLabel : fund.dateLabel) || "";
    const dateForFile = dateLabel.replace(/[^A-Za-z0-9]+/g, "-");
    const filename = `${slug}-holdings${dateForFile ? `-${dateForFile}` : ""}.csv`;

    const csv = rowsToCsv(rows, [
      { header: "Name", value: (h) => h.name },
      { header: "Ticker", value: (h) => h.ticker },
      { header: "Weight (%)", value: (h) => (h.pct == null ? "" : h.pct.toFixed(2)) },
      { header: "Market Value", value: (h) => h.marketValue ?? "" },
    ]);
    downloadCsv(filename, csv);
  }, [fund, liveHoldings, isLeveraged, leveraged]);

  const overlay = (label: string, original: string): string => {
    const which = LIVE_FACT_LABELS[label];
    if (!which) return original;
    if (which === "aum") {
      if (liveAum != null) return formatAum(liveAum);
      if (live.aum != null) return formatAum(live.aum);
      return "TBD";
    }
    if (which === "nav") {
      if (live.nav != null) return formatPrice(live.nav);
      return "TBD";
    }
    if (which === "premium") {
      if (live.premiumDiscountPct != null) return formatChangePct(live.premiumDiscountPct);
      return "TBD";
    }
    if (which === "price") {
      if (live.price != null) return formatPrice(live.price);
      return "TBD";
    }
    if (which === "spread") {
      return formatSpreadPct(live.medianSpreadPct);
    }
    return original;
  };

  const holdingsAumAsOfIso =
    liveHoldings.isLive && liveHoldings.aum != null && liveHoldings.aum > 0
      ? liveHoldings.asOfDate
      : null;
  // Prefer the LIVE daily_nav date over the static config `navAsOf` (= the
  // hand-entered, often-stale `aumAsOf`); fall back to config only pre-ingestion.
  const navSnapshotIso = live.navAsOfDate ?? fund.navAsOf;
  const navAsOf = formatFactAsOf(navSnapshotIso);
  const priceAsOf = formatFactAsOf(live.asOfDate);
  const aumAsOf = formatFactAsOf(holdingsAumAsOfIso ?? navSnapshotIso);
  const factAsOf = (label: string): string | null => {
    const which = LIVE_FACT_LABELS[label];
    if (which === "aum") return aumAsOf;
    if (which === "nav") return navAsOf;
    if (which === "price") return priceAsOf;
    return null;
  };

  return (
    <div className="space-y-0">
      <div
        className="border-b border-white/[0.13] px-7 pt-10 pb-10 max-md:px-4 max-md:pt-7 max-md:pb-7"
        id="sec-fund-facts"
        data-fund-section={FUND_FACTS_PATHS}
      >
        <SectionTitle>Fund Facts</SectionTitle>
        <div>
          {fund.fundFacts.map((fact) => {
            const asOf = factAsOf(fact.label);
            return (
              <div
                key={fact.label}
                data-fund-field={FACT_LABEL_TO_PATH[fact.label]}
                className="flex justify-between items-baseline gap-3 py-[9px] border-b border-[var(--etf-border2)] last:border-b-0 font-[family-name:var(--font-instrument)]"
              >
                <span className="text-[11px] leading-[1.5] text-[var(--etf-white2)] tracking-[0.03em] shrink-0 flex flex-col gap-px">
                  <span className="flex items-center gap-1 whitespace-nowrap overflow-hidden text-ellipsis">
                    {fact.label}
                    {fact.tooltip && <InfoTooltipIcon tip={fact.tooltip} direction="left" />}
                  </span>
                  {asOf && (
                    <span className="text-[8px] tracking-[0.04em] text-[var(--etf-white3)] opacity-60 font-normal leading-[1.3]">
                      (as of {asOf})
                    </span>
                  )}
                </span>
                <span
                  className={`text-right text-[11.5px] leading-[1.5] ${fact.highlight ? "text-[var(--etf-orange)]" : "text-[var(--etf-white)]"}`}
                >
                  {overlay(fact.label, fact.value)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {betweenFactsAndDocuments}

      {!factsOnly && (
        <div
          id="sec-documents"
          data-fund-section="documents"
          className="border-b border-white/[0.13] px-7 py-10 max-md:px-4 max-md:py-7"
        >
          <SectionTitle>Documents</SectionTitle>
          <div>
            {fund.documents.map((doc) => {
              const isHoldingsDoc = doc.name.startsWith("Daily Holdings");
              const holdingsAvailable = isHoldingsDoc
                ? isLeveraged
                  ? (leveraged.rows?.length ?? 0) > 0
                  : liveHoldings.holdings.length > 0
                : true;
              const isUnavailable = isHoldingsDoc
                ? !holdingsAvailable
                : doc.unavailable === true || isSecFiledDocRestricted(doc.name, Boolean(doc.href));
              const isHoldingsRow = !isUnavailable && isHoldingsDoc;
              const buttonHandler = isHoldingsRow ? handleDownloadHoldings : null;
              const Wrapper = isUnavailable
                ? "div"
                : buttonHandler
                  ? "button"
                  : doc.href
                    ? "a"
                    : "div";
              const linkProps = isUnavailable
                ? ({ "aria-disabled": "true" } as const)
                : buttonHandler
                  ? { type: "button" as const, onClick: buttonHandler }
                  : doc.href
                    ? { href: doc.href, target: "_blank", rel: "noopener noreferrer" }
                    : {};
              return (
                <Wrapper
                  key={doc.name}
                  {...linkProps}
                  className={cn(
                    "w-full flex items-center justify-between py-2.5 border-b border-[var(--etf-border2)] last:border-b-0 no-underline text-inherit text-left bg-transparent border-x-0 border-t-0",
                    isUnavailable
                      ? "opacity-40 cursor-not-allowed"
                      : "cursor-pointer transition-opacity duration-150 hover:opacity-80",
                  )}
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className={cn(
                        "font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] tracking-[0.06em] border px-[5px] py-[2px]",
                        isUnavailable
                          ? "text-[var(--etf-white3)] border-[var(--etf-border)] opacity-70"
                          : "text-[var(--etf-orange)] border-[var(--etf-orange)] opacity-70",
                      )}
                    >
                      {doc.type}
                    </span>
                    <div>
                      <div className="font-[family-name:var(--font-instrument)] text-[14px] leading-[1.5] text-[var(--etf-white2)]">
                        {doc.name}
                      </div>
                      {!isUnavailable && doc.date && (
                        <div className="font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] text-[var(--etf-white3)] mt-0.5">
                          {doc.date}
                        </div>
                      )}
                    </div>
                  </div>
                  <span
                    className={cn(
                      "w-7 h-7 flex items-center justify-center border rounded transition-colors bg-transparent text-[13px] shrink-0",
                      isUnavailable
                        ? "border-[var(--etf-border)] text-[var(--etf-white3)]"
                        : "border-[rgba(255,85,0,0.35)] text-[var(--etf-orange)] hover:border-[var(--etf-orange)] hover:bg-[rgba(255,85,0,0.05)]",
                    )}
                  >
                    &darr;
                  </span>
                </Wrapper>
              );
            })}
          </div>
        </div>
      )}

      {!factsOnly && <FundBrokers fund={fund} />}
    </div>
  );
}

export function FundBrokers({ fund }: { fund: FundDetail }) {
  return (
    <div
      id="sec-brokerages"
      className="px-7 pt-10 pb-10 max-md:px-4 max-md:pt-7 max-md:pb-7 scroll-mt-[120px]"
    >
      <SectionTitle>
        {fund.type === "leveraged" ? "Trade" : "Buy"} {fund.ticker}
      </SectionTitle>
      <p className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white3)] mb-5 leading-[1.6]">
        {fund.type === "leveraged" ? (
          <>
            {fund.ticker} is available through major brokerages. Search for ticker{" "}
            <strong className="text-[var(--etf-white)]">{fund.ticker}</strong>. Review the
            prospectus before trading.
          </>
        ) : (
          <>
            {fund.ticker} is available through major brokerages. Click a logo to open your account
            and search for ticker <strong className="text-[var(--etf-white)]">{fund.ticker}</strong>
            .
          </>
        )}
      </p>
      <div className="flex flex-col gap-px bg-[var(--etf-border)] border border-[var(--etf-border)]">
        {fund.brokers.map((broker) => (
          <a
            key={broker.name}
            href={broker.url}
            target="_blank"
            rel="noopener noreferrer"
            data-analytics-event="broker_click"
            data-analytics-intent="high"
            data-analytics-fund={fund.ticker}
            className="flex items-center justify-between py-3.5 px-4 bg-[var(--etf-black3)] no-underline transition-colors duration-150 hover:bg-[rgba(255,85,0,0.04)] group"
          >
            <span className="font-[family-name:var(--font-instrument)] text-[13px] font-medium tracking-[0.04em] text-[var(--etf-white2)] transition-colors group-hover:text-[var(--etf-white)]">
              {broker.name}
            </span>
            <span className="font-[family-name:var(--font-instrument)] text-[14px] leading-[1.5] text-[var(--etf-white3)] transition-all group-hover:text-[var(--etf-orange)] group-hover:translate-x-0.5">
              &rarr;
            </span>
          </a>
        ))}
      </div>
      <p className="font-[family-name:var(--font-instrument)] text-[10px] text-[var(--etf-white3)] mt-3 leading-[1.6] opacity-60">
        Corgi ETFs are available through various channels. Reach out to your Financial Advisor for
        more information. Corgi Funds is not affiliated with these financial services firms.
      </p>
    </div>
  );
}

export function renderInline(text: string): React.ReactNode {
  const tokens: React.ReactNode[] = [];
  const re = /\*\*_(.+?)_\*\*|\*\*(.+?)\*\*|_(.+?)_/g;
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null = re.exec(text);
  while (match !== null) {
    if (match.index > lastIndex) {
      tokens.push(text.slice(lastIndex, match.index));
    }
    if (match[1] !== undefined) {
      tokens.push(
        <strong key={key++} className="text-[var(--etf-white2)] italic">
          {match[1]}
        </strong>,
      );
    } else if (match[2] !== undefined) {
      tokens.push(
        <strong key={key++} className="text-[var(--etf-white2)]">
          {match[2]}
        </strong>,
      );
    } else if (match[3] !== undefined) {
      tokens.push(<em key={key++}>{match[3]}</em>);
    }
    lastIndex = re.lastIndex;
    match = re.exec(text);
  }
  if (lastIndex < text.length) {
    tokens.push(text.slice(lastIndex));
  }
  return tokens;
}
